import React, { useRef, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { ColspanTd, Container, Table, Td, Th, Tr } from './InvoiceDialog.styled';
import { getFormatStandardDate } from '../../utils/helpers/date';

interface InvoiceDialogProps {
  open: boolean;
  handleClose: () => void;
  orderData: any;
}

interface OrderItem {
  product: {
    name: string;
    code: string;
    price: number;
    discount: number;
    gst: number;
  };
  qty: number;
}

interface InvoiceTotals {
  subtotal: number;
  totalGst: number;
  deliveryCharge: number;
  grandTotal: number;
}

interface ProcessedOrderItem extends OrderItem {
  calculatedPrice: number;
  discountAmount: number;
  gstAmount: number;
  itemTotal: number;
  lineTotal: number;
}

const calculateItemDetails = (item: OrderItem): ProcessedOrderItem => {
  const price = Math.round(item.product.price);
  const discount = isNaN(Number(item.product.discount)) ? 0 : Math.round(item.product.discount);
  const discountAmount = Math.round((discount * price) / 100);
  const gst = Math.round(item.product.gst);
  const gstAmount = Math.round((gst * price) / 100);
  const itemTotal = Math.round(price - discountAmount + gstAmount);
  const lineTotal = Math.round(itemTotal * item.qty);

  return {
    ...item,
    calculatedPrice: price,
    discountAmount,
    gstAmount,
    itemTotal,
    lineTotal,
  };
};

const calculateInvoiceTotals = (items: OrderItem[], totalAmount: number): InvoiceTotals => {
  const processedItems = items.map(calculateItemDetails);
  
  const subtotal = processedItems.reduce((sum, item) => sum + item.lineTotal, 0);
  const totalGst = processedItems.reduce((sum, item) => sum + (item.gstAmount * item.qty), 0);
  const deliveryCharge = Math.round(totalAmount - subtotal);
  const grandTotal = Math.round(totalAmount);

  return {
    subtotal,
    totalGst,
    deliveryCharge,
    grandTotal,
  };
};

const InvoiceContent: React.FC<{ orderData: any }> = ({ orderData }) => {
  const customer = orderData?.userData;
  const shippingAddress = orderData?.addressDetails;
  
  const processedItems = useMemo(
    () => orderData?.productDetails?.map(calculateItemDetails) || [],
    [orderData?.productDetails]
  );

  const totals = useMemo(
    () => calculateInvoiceTotals(orderData?.productDetails || [], orderData?.totalAmount || 0),
    [orderData?.productDetails, orderData?.totalAmount]
  );
  return (
    <Container>
      <Table>
        <tbody>
          <Tr>
            <ColspanTd colSpan={4}>
              <strong>Customer Address</strong>
              <br />
              {customer?.name}
              <br />
              Email: {customer?.email}
              <br />
              Number: {customer?.mobileNumber}
              <br />
              {customer?.city}, {customer?.state}, {customer?.country}, {customer?.pinCode}.
            </ColspanTd>
            <ColspanTd colSpan={4}>
              <strong>If undelivered, return to:</strong>
              <br />
              VR FASHION
              <br />
              SY.No.68,Plot -5 F Flr GhanshyamNagar
              <br />
              Seri No.2 Baroda Pristej, LabheSvar Police Choki Ni Pase Varachha Road, Surat, Gujarat, 395006.
              <br />
              
            </ColspanTd>
          </Tr>
          <Tr>
            <ColspanTd colSpan={4}>
              <strong>BILL TO / SHIP TO</strong>
              <br />
              {customer?.name} - {shippingAddress?.addressFirst}, {shippingAddress?.area}, {shippingAddress?.landmark}, {shippingAddress?.city}, {shippingAddress?.state}, {shippingAddress?.country}, {shippingAddress?.pinCode}, Place of Supply: {shippingAddress?.state}
            </ColspanTd>
            <ColspanTd colSpan={4}>
              Sold by: {customer?.name}
              <br />
              SY.No.68, Plot -5 Gr Flr GhanshyamNagar-2
              <br />
              <strong>GSTIN: 24AMKPB4330Q1ZZ</strong>
            </ColspanTd>
          </Tr>
          <Tr>
            <ColspanTd colSpan={8}>
              <Table>
                <tbody>
                  <Tr>
                    <Th colSpan={8}>Order Details</Th>
                  </Tr>
                  <Tr>
                    <Th colSpan={3}>Payment Id</Th>
                    <Th colSpan={2}>Total Product</Th>
                    <Th colSpan={3}>Date</Th>
                  </Tr>
                  <Tr>
                    <Td colSpan={3}>{orderData?.paymentId || 'COD'}</Td>
                    <Td colSpan={2}>{orderData?.productDetails?.length || 0}</Td>
                    <Td colSpan={3}>{getFormatStandardDate(orderData?.createdAt)}</Td>
                  </Tr>
                </tbody>
              </Table>
            </ColspanTd>
          </Tr>
          <Tr>
            <ColspanTd colSpan={8}>
              <Table>
                <tbody>
                  <Tr>
                    <Th>Name</Th>
                    <Th>HSN</Th>
                    <Th>Qty</Th>
                    <Th>Taxable Value</Th>
                    <Th>Discount</Th>
                    <Th>Gross Amount</Th>
                    <Th>Taxes</Th>
                    <Th>Total</Th>
                  </Tr>
                  {processedItems.map((item: ProcessedOrderItem, index: number) => (
                    <Tr key={`${item.product.code}-${index}`}>
                      <Td>{item.product.name}</Td>
                      <Td>{item.product.code}</Td>
                      <Td>{item.qty}</Td>
                      <Td>Rs.{item.calculatedPrice}</Td>
                      <Td>Rs.{item.discountAmount}</Td>
                      <Td>Rs.{item.itemTotal}</Td>
                      <Td>
                        GST @{item.product.gst}%
                        <br />
                        Rs.{item.gstAmount}
                      </Td>
                      <Td>Rs.{item.lineTotal}</Td>
                    </Tr>
                  ))}
                  <Tr>
                    <Th colSpan={6}>Total</Th>
                    <Td>Rs.{totals.totalGst}</Td>
                    <Td>Rs.{totals.subtotal}</Td>
                  </Tr>
                  <Tr>
                    <Th colSpan={7}>Delivery Charge</Th>
                    <Td>Rs.{totals.deliveryCharge}</Td>
                  </Tr>
                  <Tr>
                    <Th colSpan={7}>Grand Total</Th>
                    <Td>Rs.{totals.grandTotal}</Td>
                  </Tr>
                </tbody>
              </Table>
            </ColspanTd>
          </Tr>
        </tbody>
      </Table>
    </Container>
  );
};

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({ open, handleClose, orderData }) => {
  const invoiceContentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => invoiceContentRef.current,
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="body">
      <DialogTitle sx={{ background: '#0a3156', color: '#ffffff' }}>
        Invoice
      </DialogTitle>
      <DialogContent>
        <div ref={invoiceContentRef}>
          <InvoiceContent orderData={orderData} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handlePrint} color="primary">
          Print
        </Button>
        <Button variant="contained" onClick={handleClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDialog;